import { ApiError, apiJson } from '@/api/apiFetch';
import { NEWSFEED_COMMENTS_PAGE, buildFeedQuery, toFeedPage, toNewsfeedItem, withLocale } from './Newsfeed.normalize';
import type {
  CommentsResponse,
  CreatePostPayload,
  GetFeedResponse,
  NewsfeedComment,
  NewsfeedItem,
  NewsfeedItemResponse,
  NewsfeedTransport,
  PostConfirmations,
  RemindConfirmationsResponse,
  TranslatePostResponse,
  UpdatePostPayload,
} from './Newsfeed.types';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export class InvalidPostIdError extends Error {
  constructor() {
    super('invalid-post-id');
    this.name = 'InvalidPostIdError';
  }
}

// Post ids reach these paths from the route (`/newsfeed/:postId`) and deep links: anything but a
// UUID is refused before it can reshape the API path.
const postPath = (postId: string): string => {
  const id = String(postId);
  if (!UUID_PATTERN.test(id)) {
    throw new InvalidPostIdError();
  }
  return `/newsfeed/posts/${encodeURIComponent(id)}`;
};

// Async so an invalid id rejects the returned promise like any other API failure.
const withPostPath = <T>(postId: string, request: (path: string) => Promise<T>): Promise<T> =>
  Promise.resolve().then(() => request(postPath(postId)));

/** The newsfeed on khova-api (contract §9), UUID string ids. */
export const khovaNewsfeedTransport: NewsfeedTransport = {
  fetchFeed: async (params) => toFeedPage(await apiJson<GetFeedResponse>(`/newsfeed/feed?${buildFeedQuery(params)}`)),
  fetchPost: (postId, locale) =>
    withPostPath(postId, async (path) => toNewsfeedItem(await apiJson<NewsfeedItemResponse>(withLocale(path, locale)))),
  markRead: (postId) => withPostPath(postId, (path) => apiJson<void>(`${path}/read`, { method: 'POST' })),
  setLike: (item, like) =>
    withPostPath(item.id, (path) =>
      apiJson<{ like: boolean; postId: string; likes: number }>(`${path}/like`, {
        method: 'POST',
        body: JSON.stringify({ like }),
      }),
    ),
  answerPoll: (postId, answerId) =>
    withPostPath(postId, (path) =>
      apiJson<void>(`${path}/answers/${encodeURIComponent(answerId)}`, { method: 'POST' }),
    ),
  fetchComments: (postId) =>
    withPostPath(postId, (path) => apiJson<CommentsResponse>(`${path}/comments?${NEWSFEED_COMMENTS_PAGE}`)),
  addComment: (postId, content) =>
    withPostPath(postId, (path) =>
      apiJson<NewsfeedComment>(`${path}/comments`, {
        method: 'POST',
        body: JSON.stringify({ content }),
      }),
    ),
  translatePost: async (postId, locale) => {
    try {
      return await withPostPath(postId, (path) =>
        apiJson<TranslatePostResponse>(`${path}/translations/${encodeURIComponent(locale)}`, {
          method: 'POST',
        }),
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        return null;
      }
      throw err;
    }
  },
};

export const createKhovaNewsfeedPost = async (payload: CreatePostPayload): Promise<NewsfeedItem> =>
  toNewsfeedItem(
    await apiJson<NewsfeedItemResponse>('/newsfeed/posts', { method: 'POST', body: JSON.stringify(payload) }),
  );

export const updateKhovaNewsfeedPost = (postId: string, payload: UpdatePostPayload): Promise<NewsfeedItem> =>
  withPostPath(postId, async (path) =>
    toNewsfeedItem(await apiJson<NewsfeedItemResponse>(path, { method: 'PATCH', body: JSON.stringify(payload) })),
  );

export const deleteKhovaNewsfeedPost = (postId: string): Promise<void> =>
  withPostPath(postId, (path) => apiJson<void>(path, { method: 'DELETE' }));

export const fetchPendingConfirmations = async (locale: string | null): Promise<NewsfeedItem[]> =>
  (await apiJson<NewsfeedItemResponse[]>(withLocale('/newsfeed/confirmations/pending', locale))).map(toNewsfeedItem);

export const confirmKhovaNewsfeedPost = (postId: string): Promise<void> =>
  withPostPath(postId, (path) => apiJson<void>(`${path}/confirm`, { method: 'POST' }));

export const fetchPostConfirmations = (postId: string, storeId: string | null): Promise<PostConfirmations> =>
  withPostPath(postId, (path) =>
    apiJson<PostConfirmations>(
      storeId ? `${path}/confirmations?storeId=${encodeURIComponent(storeId)}` : `${path}/confirmations`,
    ),
  );

export const remindPostConfirmations = (postId: string, storeId: string | null): Promise<RemindConfirmationsResponse> =>
  withPostPath(postId, (path) =>
    apiJson<RemindConfirmationsResponse>(`${path}/confirmations/remind`, {
      method: 'POST',
      body: JSON.stringify({ storeId }),
    }),
  );
